import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { ReactFlowProvider } from 'reactflow';
import { Play, Pause, Save } from 'lucide-react';
import { AutomationStatus, AutomationActionNodeSubType } from '@frontend/app/types/globalTypes';
import {
  useCreateAutomationMutation,
  useCreateRevisionMutation,
  useGetAutomationQuery,
  useGetTemplateQuery,
  useGetNodeOptionsQuery
} from '@frontend/app/hooks/automations';
import omitDeep from 'omit-deep-lodash';

import { AutomationNodeType } from '@frontend/app/types/globalTypes';
import { Button } from '@/shadcn/components/ui/button';
import { Badge } from '@/shadcn/components/ui/badge';
import { FlowCanvas } from './FlowCanvas';
import { FloatingNodePalette } from './FloatingNodePalette';
import { PropertyPanel } from './PropertyPanel';
import { TemplateSelectionModal } from './TemplateSelectionModal';
import {
  AutomationNodeV2,
  AutomationEditorState,
  AutomationEditorActions,
  NodeTemplate,
} from './types';
import {
  findNodeById,
  updateNode,
  removeNode,
  moveNode,
  cloneNode,
  generateNodeId,
  validateAutomationTree,
  getAllNodes,
  convertTemplateToV2Blueprint,
  ensureNodeIds,
  insertNodeInline,
  addBranchNode,
  replacePlaceholderNode,
  ensureConditionSubTypes
} from './utils';
import { useMessagingContext } from '@/hooks';
import { useAutomationCommunicationStandalone } from './hooks';
import { DEFAULT_ACTIONS, DEFAULT_DELAY_MS } from './constants';

interface AutomationBuilderV2Props {
  automationId?: string;
  templateId?: string;
  projectId: number;
  onSave?: () => void;
  onCancel?: () => void;
  readOnly?: boolean;
}

export const AutomationBuilderV2: React.FC<AutomationBuilderV2Props> = ({
  automationId,
  templateId: initialTemplateId,
  projectId,
  onSave,
  onCancel,
  readOnly = false,
}) => {
  // Hooks
  const { showSuccessMessage, showErrorMessage } = useMessagingContext();
  const [createAutomation, { loading: isCreating }] = useCreateAutomationMutation();
  const [createRevision, { loading: isUpdating }] = useCreateRevisionMutation();
  const { sendBlueprintToAVA, onBlueprintEvent } = useAutomationCommunicationStandalone({
    projectId,
    onBlueprintReceived: (_blueprint) => {
      // TODO: Implement blueprint received from AVA
    },
    onBlueprintSent: (_blueprint) => {
      // TODO: Implement blueprint sent to AVA
    },
  });

  // State for template selection
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(initialTemplateId || null);
  const [showTemplateSelection, setShowTemplateSelection] = useState<boolean>(!automationId && !initialTemplateId);

  const {
    data: { automation } = { automation: null },
    loading: isLoadingAutomation,
    refetch: refetchAutomation,
  } = useGetAutomationQuery({
    variables: { automationId },
    skip: !automationId,
  });

  // Listen for blueprint events from AVA
  useEffect(() => {
    const unsubscribe = onBlueprintEvent((event) => {
      if (event.type === 'BLUEPRINT_IMPORTED' && event.payload.blueprint) {
        // Import blueprint from AVA
        const importedBlueprint = event.payload.blueprint;

        // Update the editor state with the imported blueprint
        setEditorState((prev) => ({
          ...prev,
          blueprint: {
            ...importedBlueprint,
            programId: projectId, // Ensure projectId is set correctly
          },
          selectedNodeId: null,
          hasUnsavedChanges: true, // Mark as having unsaved changes
        }));

        showSuccessMessage('Blueprint imported from AVA successfully');
      }
    });

    return unsubscribe;
  }, [onBlueprintEvent, showSuccessMessage, projectId]);

  // Load template data

  const {
    data,
    loading: isLoadingTemplate,
  } = useGetTemplateQuery({
    variables: {
      templateId: selectedTemplateId || '',
    },
    skip: !selectedTemplateId,
  });

  const template = data?.template?.blueprint;

  // Load node options (action and condition options)
  const {
    data: {
      getNodeOptions: { actionOptions = [], conditionOptions = [] } = { actionOptions: [], conditionOptions: [] },
    } = { getNodeOptions: { actionOptions: [], conditionOptions: [] } },
    loading: isLoadingNodeOptions,
  } = useGetNodeOptionsQuery({
    variables: {
      context: {
        automationId,
        templateId: selectedTemplateId,
      },
    },
    skip: !selectedTemplateId && !automationId,
  });

  // Initialize state with empty blueprint
  const [editorState, setEditorState] = useState<AutomationEditorState>({
    blueprint: {
      id: automationId,
      name: '',
      description: '',
      triggers: [],
      programId: projectId,
      templateId: selectedTemplateId,
      metadata: { icon: 'workflow' }
    },
    selectedNodeId: null,
    isEditing: !readOnly,
    hasUnsavedChanges: false,
    draggedNodeType: null,
    clipboard: null,
  });

  // Effect to initialize blueprint from template
  useEffect(() => {
    if (template && selectedTemplateId) {
      try {
        // Convert the template blueprint to V2 structure with proper node rendering
        const convertedBlueprint = convertTemplateToV2Blueprint(template, projectId);

        setEditorState((prev) => ({
          ...prev,
          blueprint: convertedBlueprint,
          selectedNodeId: null,
          hasUnsavedChanges: false,
        }));
      } catch (error) {
        showErrorMessage('Failed to load template. Please try again.');

        // Fallback to empty blueprint
        setEditorState((prev) => ({
          ...prev,
          blueprint: {
            id: undefined,
            name: template.name || 'Untitled Automation',
            description: template.description || '',
            triggers: [],
            programId: projectId,
            templateId: selectedTemplateId,
            metadata: template.metadata || { icon: 'workflow' }
          },
          selectedNodeId: null,
        }));
      }
    }
  }, [template, selectedTemplateId, projectId, showErrorMessage]);

  // Effect to initialize blueprint from automation (simplified for UI testing)
  useEffect(() => {
    if (automation) {
      // Loading existing automation
      const automationBlueprint = omitDeep(automation, '__typename');
      setEditorState((prev) => ({
        ...prev,
        blueprint: {
          id: automationBlueprint.id,
          name: automationBlueprint.name,
          description: automationBlueprint.description,
          triggers: automationBlueprint.triggers,
          programId: projectId,
          templateId: automationBlueprint.templateId,
          metadata: automationBlueprint.metadata || { icon: 'workflow' }
        },
        isEditing: !readOnly,
        // Select the trigger node by default
        selectedNodeId: automationBlueprint.triggers[0]?.id,
      }));
    }
  }, [automation, projectId, readOnly]);

  // Template selection handlers
  const handleTemplateSelect = useCallback((templateId: string) => {
    // Update state (React 17 will batch these automatically in event handlers)
    setSelectedTemplateId(templateId);
    setShowTemplateSelection(false);
  }, []);

  const handleCancelTemplateSelection = useCallback(() => {
    if (onCancel) {
      onCancel();
    }
  }, [onCancel]);

  // Derived state
  const currentTrigger = editorState.blueprint.triggers[0] || null;
  const selectedNode = editorState.selectedNodeId && currentTrigger?.root
    ? findNodeById(currentTrigger.root, editorState.selectedNodeId)
    : null;

  // Check if the selected node is the root of the automation tree
  const isSelectedNodeRoot =
    !!currentTrigger?.root && editorState.selectedNodeId === currentTrigger.root.id;

  // Actions
  const actions: AutomationEditorActions = useMemo(() => ({
    setSelectedNode: (nodeId: string | null) => {
      setEditorState((prev) => ({
        ...prev,
        selectedNodeId: nodeId,
      }));
    },

    updateNode: (nodeId: string, updates: Partial<AutomationNodeV2>) => {
      setEditorState((prev) => ({
        ...prev,
        blueprint: {
          ...prev.blueprint,
          triggers: prev.blueprint.triggers.map((trigger) => ({
            ...trigger,
            root: updateNode(trigger.root, nodeId, updates),
          })),
        },
        hasUnsavedChanges: true,
      }));
    },

    addNode: (parentId: string, newNode: AutomationNodeV2, isElseBranch?: boolean, isBranch?: boolean) => {
      setEditorState((prev) => {
        const updateFunction = isBranch ? addBranchNode : insertNodeInline;

        return {
          ...prev,
          blueprint: {
            ...prev.blueprint,
            triggers: prev.blueprint.triggers.map((trigger) => ({
              ...trigger,
              root: updateFunction(trigger.root, parentId, newNode, isElseBranch),
            })),
          },
          hasUnsavedChanges: true,
          // Don't auto-select placeholder nodes to prevent PropertyPanel from opening
          selectedNodeId: newNode.metadata?.isPlaceholder ? null : newNode.id,
        };
      });
    },

    replacePlaceholder: (placeholderId: string, newNode: AutomationNodeV2) => {
      setEditorState((prev) => ({
        ...prev,
        blueprint: {
          ...prev.blueprint,
          triggers: prev.blueprint.triggers.map((trigger) => ({
            ...trigger,
            root: replacePlaceholderNode(trigger.root, placeholderId, newNode),
          })),
        },
        hasUnsavedChanges: true,
        selectedNodeId: newNode.id,
      }));
    },

    deleteNode: (nodeId: string) => {
      setEditorState((prev) => ({
        ...prev,
        blueprint: {
          ...prev.blueprint,
          triggers: prev.blueprint.triggers.map((trigger) => ({
            ...trigger,
            root: removeNode(trigger.root, nodeId) || trigger.root,
          })),
        },
        hasUnsavedChanges: true,
        selectedNodeId: prev.selectedNodeId === nodeId ? null : prev.selectedNodeId,
      }));
    },

    moveNode: (nodeId: string, newParentId: string, index?: number) => {
      setEditorState((prev) => ({
        ...prev,
        blueprint: {
          ...prev.blueprint,
          triggers: prev.blueprint.triggers.map((trigger) => ({
            ...trigger,
            root: moveNode(trigger.root, nodeId, newParentId, index),
          })),
        },
        hasUnsavedChanges: true,
      }));
    },

    copyNode: (nodeId: string) => {
      const nodeToCopy = findNodeById(currentTrigger.root, nodeId);
      if (nodeToCopy) {
        setEditorState((prev) => ({
          ...prev,
          clipboard: cloneNode(nodeToCopy, false), // Don't generate new IDs for clipboard
        }));
      }
    },

    pasteNode: (parentId: string) => {
      if (editorState.clipboard) {
        const clonedNode = cloneNode(editorState.clipboard, true); // Generate new IDs when pasting
        actions.addNode(parentId, clonedNode);
      }
    },

    setEditMode: (editing: boolean) => {
      setEditorState((prev) => ({
        ...prev,
        isEditing: editing,
      }));
    },

    saveBlueprint: async () => {
      // Prepare the blueprint for API following the existing pattern
      const blueprintForAPI = automationId ? {
        // For updates - use blueprintInput format
        id: editorState.blueprint.id,
        name: editorState.blueprint.name,
        description: editorState.blueprint.description,
        programId: projectId,
        metadata: editorState.blueprint.metadata,
        triggers: editorState.blueprint.triggers.map((trigger) => ({
          ...trigger,
          root: ensureNodeIds(ensureConditionSubTypes({
            ...trigger.root,
            metadata: {
              ...trigger.root.metadata,
              brandCatalogId: trigger.root.metadata?.brandCatalogId,
              selectionRuleId: trigger.root.metadata?.selectionRuleId,
            }
          }))
        })),
      } : {
        // For creation - use full blueprint format
        ...editorState.blueprint,
        programId: projectId,
        triggers: editorState.blueprint.triggers.map((trigger) => ({
          ...trigger,
          root: ensureNodeIds(ensureConditionSubTypes({
            ...trigger.root,
            metadata: {
              ...trigger.root.metadata,
              brandCatalogId: trigger.root.metadata?.brandCatalogId,
              selectionRuleId: trigger.root.metadata?.selectionRuleId,
            }
          }))
        })),
      };

      try {
        // Validate the automation tree
        if (!currentTrigger?.root) {
          showErrorMessage('No automation trigger found. Please select a template first.');
          return;
        }

        const errors = validateAutomationTree(currentTrigger.root);

        // Additional validation for API requirements
        const apiValidationErrors: string[] = [];
        const allNodes = getAllNodes(currentTrigger.root);

        allNodes.forEach((node) => {
          if (node.type === AutomationNodeType.ACTION) {
            if (!node.metadata.resourceId) {
              apiValidationErrors.push(`Action node ${node.id}: resourceId is required`);
            }
            if (node.metadata.fromPreviousSender === undefined) {
              apiValidationErrors.push(`Action node ${node.id}: fromPreviousSender is required`);
            }
            if (node.subType === AutomationActionNodeSubType.SEND_EMAIL && !node.metadata.templateId) {
              apiValidationErrors.push(`Email action node ${node.id}: templateId is required`);
            }
          }

          if (node.type === AutomationNodeType.CONDITION) {
            if (!node.metadata.condition) {
              apiValidationErrors.push(`Condition node ${node.id}: condition metadata is required`);
            }
          }

          if (node.type === AutomationNodeType.DELAY) {
            if (!node.metadata.durationMs || node.metadata.durationMs <= 0) {
              apiValidationErrors.push(`Delay node ${node.id}: durationMs must be greater than 0`);
            }
          }
        });

        const allValidationErrors = [...errors, ...apiValidationErrors];
        if (allValidationErrors.length > 0) {
          showErrorMessage(`Validation errors: ${allValidationErrors.join('; ')}`);
          return;
        }

        if (automationId) {
          // Update existing automation
          await createRevision({
            variables: {
              blueprint: blueprintForAPI,
              options: {
                publish: automation?.status === AutomationStatus.ACTIVE,
              },
            },
            onCompleted() {
              refetchAutomation();
              showSuccessMessage('Successfully updated automation');
            },
            onError(error) {
              // Handle errors gracefully like the existing system
              error.graphQLErrors = [];
              throw error;
            },
          });
        } else {
          // Create new automation
          await createAutomation({
            variables: {
              blueprint: blueprintForAPI,
              options: {
                publish: true,
              },
            },
            onCompleted() {
              showSuccessMessage('Successfully created automation');
            },
            onError(error) {
              error.graphQLErrors = [];
              throw error;
            },
          });

          if (onSave) {
            onSave();
          }
          // Send updated blueprint to AVA
          sendBlueprintToAVA(editorState.blueprint, {
            source: 'AUTOMATION_BUILDER',
            timestamp: Date.now(),
            action: 'saved'
          });
        }

        setEditorState((prev) => ({
          ...prev,
          hasUnsavedChanges: false,
        }));
      } catch (error) {
        let errorMessage = 'Failed to save automation';
        if (error.graphQLErrors && error.graphQLErrors.length > 0) {
          errorMessage = error.graphQLErrors.map((err) => err.message).join('; ');
        } else if (error.networkError) {
          errorMessage = `Network error: ${error.networkError.message}`;
        } else if (error.message) {
          errorMessage = error.message;
        }

        showErrorMessage(errorMessage);
      }
    },
  }), [
    editorState.clipboard,
    currentTrigger?.root,
    automationId,
    projectId,
    automation?.status,
    createRevision,
    createAutomation,
    showSuccessMessage,
    showErrorMessage,
    onSave,
    refetchAutomation,
    editorState.blueprint,
    sendBlueprintToAVA,
  ]);

  // Event handlers
  const handleNodeClick = useCallback((nodeId: string) => {
    actions.setSelectedNode(nodeId);
  }, [actions]);

  const handleUpdateNode = useCallback((nodeId: string, updates: Partial<AutomationNodeV2>) => {
    actions.updateNode(nodeId, updates);
  }, [actions]);

  const handleAddChild = useCallback((parentId: string, template: NodeTemplate, isElseBranch?: boolean, isBranch?: boolean) => {
    // Ensure proper default metadata based on node type
    let defaultMetadata = { ...template.defaultMetadata };

    if (template.type === 'ACTION') {
      defaultMetadata = {
        ...defaultMetadata,
        resourceId: DEFAULT_ACTIONS.RESOURCE_ID,
        fromPreviousSender: false,
        templateId: template.subType === AutomationActionNodeSubType.SEND_EMAIL
          ? DEFAULT_ACTIONS.DEFAULT_EMAIL_TEMPLATE_ID
          : undefined,
      };
    } else if (template.type === 'CONDITION') {
      defaultMetadata = {
        ...defaultMetadata,
        condition: null // Start with empty condition - user must configure
      };
    } else if (template.type === 'DELAY') {
      defaultMetadata = {
        ...defaultMetadata,
        durationMs: DEFAULT_DELAY_MS, // Default 1 hour
      };
    }

    const newNode: AutomationNodeV2 = {
      id: generateNodeId(),
      type: template.type,
      subType: template.subType,
      children: [],
      elseChildren: template.type === 'CONDITION' ? [] : undefined,
      metadata: defaultMetadata,
      variables: [],
    };

    // Pass isElseBranch and isBranch to actions.addNode
    actions.addNode(parentId, newNode, isElseBranch, isBranch);
  }, [actions]);

  const handleDeleteNode = useCallback((nodeId: string) => {
    actions.deleteNode(nodeId);
  }, [actions]);

  const handleToggleEdit = useCallback(() => {
    if (readOnly) return;
    actions.setEditMode(!editorState.isEditing);
  }, [readOnly, editorState.isEditing, actions]);

  // Show template selection modal only when explicitly shown
  if (showTemplateSelection) {
    return (
      <TemplateSelectionModal
        isOpen
        onClose={handleCancelTemplateSelection}
        onSelectTemplate={handleTemplateSelect}
        projectId={projectId}
      />
    );
  }

  // Loading states
  if (isLoadingAutomation || isLoadingTemplate || isLoadingNodeOptions) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4" />
          <p className="text-grey-5">
            {isLoadingAutomation && 'Loading automation...'}
            {isLoadingTemplate && 'Loading template...'}
            {isLoadingNodeOptions && 'Loading options...'}
          </p>
        </div>
      </div>
    );
  }

  // Don't render editor until we have a trigger
  if (!currentTrigger) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <p className="text-grey-5">No automation trigger found. Please select a template.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full bg-white flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-grey-2 px-6 py-4 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h1 className="text-xl font-medium text-grey-6">
              {editorState.blueprint.name}
            </h1>
            {automation && (
              <Badge variant={automation.status === AutomationStatus.ACTIVE ? 'default' : 'outline'}>
                {automation.status === AutomationStatus.ACTIVE ? (
                  <>
                    <Play className="h-3 w-3 mr-1" />
                    Active
                  </>
                ) : (
                  <>
                    <Pause className="h-3 w-3 mr-1" />
                    Paused
                  </>
                )}
              </Badge>
            )}
            {editorState.hasUnsavedChanges && (
              <Badge variant="outline" className="text-orange-6 border-orange-6">
                Unsaved Changes
              </Badge>
            )}
          </div>

          <div className="flex items-center gap-2">
            {!readOnly && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleToggleEdit}
                  disabled={isCreating || isUpdating}
                >
                  {editorState.isEditing ? 'View Mode' : 'Edit Mode'}
                </Button>

                <Button
                  onClick={actions.saveBlueprint}
                  disabled={!editorState.hasUnsavedChanges || isCreating || isUpdating}
                  className="bg-blue-500 hover:bg-blue-400"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {isCreating || isUpdating ? 'Saving...' : 'Save'}
                </Button>
              </>
            )}

            {onCancel && (
              <Button variant="outline" onClick={onCancel}>
                {readOnly ? 'Close' : 'Cancel'}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden relative">
        {/* Center - Flow Canvas */}
        <div className="flex-1 relative bg-grey-1">
          <ReactFlowProvider>
            <FlowCanvas
              trigger={currentTrigger}
              isEditing={editorState.isEditing}
              selectedNodeId={editorState.selectedNodeId}
              onNodeClick={handleNodeClick}
              onUpdateNode={handleUpdateNode}
              onAddChild={handleAddChild}
              onDeleteNode={handleDeleteNode}
              actions={actions}
              automationId={automationId}
              templateId={selectedTemplateId}
              actionOptions={actionOptions}
            />
          </ReactFlowProvider>
        </div>

        {/* Right Panel - Properties (when node selected and not a placeholder) */}
        {selectedNode && editorState.isEditing && !selectedNode.metadata?.isPlaceholder && (
          <div className="w-96 h-full border-l border-grey-2 bg-white z-10 shadow-lg">
            <PropertyPanel
              selectedNode={selectedNode}
              onUpdateNode={actions.updateNode}
              onDeleteNode={actions.deleteNode}
              onCopyNode={actions.copyNode}
              canPaste={!!editorState.clipboard}
              onPasteNode={actions.pasteNode}
              onClose={() => actions.setSelectedNode(null)}
              actionOptions={actionOptions}
              conditionOptions={conditionOptions}
              automationVariables={editorState.blueprint.triggers[0]?.variables || []}
              onUpdateMessageTemplate={(_template) => {
                // TODO: Implement message template update logic
              }}
              resetMessageTemplate={false}
              isRoot={isSelectedNodeRoot}
            />
          </div>
        )}

        {/* Floating Node Palette */}
        {editorState.isEditing && (
          <FloatingNodePalette />
        )}
      </div>
    </div>
  );
};
